/**
 * Rules Data - Shared constants for the Rule Builder UI.
 * Property catalog, event types, operators, and helper functions.
 * Ported from template-forge/src/data/rulesData.ts
 */

import type { EventType, Condition, ConditionGroup, Rule } from '../types/rule.types';

// ===== Event Types =====

export interface EventTypeDefinition {
  id: EventType;
  label: string;
  description: string;
  category: 'billing' | 'payment' | 'trust';
}

export const eventTypes: EventTypeDefinition[] = [
  { id: 'bill_created', label: 'Bill Created', description: 'A new bill has been created in Clio', category: 'billing' },
  { id: 'bill_updated', label: 'Bill Updated', description: 'An existing bill has been modified', category: 'billing' },
  { id: 'bill_issued', label: 'Bill Issued', description: 'A bill has been issued to the client', category: 'billing' },
  { id: 'bill_reminder_eligible', label: 'Bill Reminder Eligible', description: 'A bill is eligible for a payment reminder', category: 'billing' },
  { id: 'payment_applied', label: 'Payment Applied', description: 'A payment has been applied to a bill', category: 'payment' },
  { id: 'allocation_updated', label: 'Allocation Updated', description: 'A payment allocation has been updated', category: 'payment' },
  { id: 'trust_request_needed', label: 'Trust Request Needed', description: 'A trust replenishment request is needed', category: 'trust' },
  { id: 'final_bill_notice_eligible', label: 'Final Bill Notice Eligible', description: 'A bill qualifies for final notice', category: 'billing' },
];

// ===== Property Catalog =====

export type PropertyType = 'text' | 'number' | 'currency' | 'date' | 'boolean' | 'picklist';

export interface PropertyDefinition {
  id: string;
  label: string;
  group: PropertyGroup;
  type: PropertyType;
  picklistValues?: string[];
  description?: string;
}

export type PropertyGroup = 'matter' | 'matter_custom_fields' | 'contact' | 'bill_info' | 'allocations' | 'system';

export const propertyGroupLabels: Record<PropertyGroup, string> = {
  matter: 'Matter',
  matter_custom_fields: 'Matter Custom Fields',
  contact: 'Contact',
  bill_info: 'Bill Info',
  allocations: 'Allocations / Payments',
  system: 'System / Derived',
};

export const propertyCatalog: PropertyDefinition[] = [
  // Matter
  { id: 'matter.status', label: 'Matter Status', group: 'matter', type: 'picklist', picklistValues: ['Open', 'Pending', 'Closed'] },
  { id: 'matter.practice_area', label: 'Practice Area', group: 'matter', type: 'picklist', picklistValues: ['Insurance Defense', 'Personal Injury', 'Corporate', 'Family Law', 'Real Estate'] },
  { id: 'matter.responsible_attorney', label: 'Responsible Attorney', group: 'matter', type: 'text' },
  { id: 'matter.open_date', label: 'Open Date', group: 'matter', type: 'date' },
  { id: 'matter.close_date', label: 'Close Date', group: 'matter', type: 'date' },
  { id: 'matter.client_name', label: 'Client Name', group: 'matter', type: 'text' },
  { id: 'matter.matter_number', label: 'Matter Number', group: 'matter', type: 'text' },
  // Matter Custom Fields
  { id: 'custom.carrier', label: 'Carrier', group: 'matter_custom_fields', type: 'picklist', picklistValues: ['State Farm', 'Allstate', 'GEICO', 'Progressive', 'Liberty Mutual'] },
  { id: 'custom.claim_number', label: 'Claim Number', group: 'matter_custom_fields', type: 'text' },
  { id: 'custom.office', label: 'Office', group: 'matter_custom_fields', type: 'picklist', picklistValues: ['Boston', 'New York', 'Chicago', 'Los Angeles'] },
  { id: 'custom.team', label: 'Team', group: 'matter_custom_fields', type: 'picklist', picklistValues: ['Team A', 'Team B', 'Team C'] },
  { id: 'custom.matter_category', label: 'Matter Category', group: 'matter_custom_fields', type: 'picklist', picklistValues: ['Litigation', 'Transactional', 'Advisory'] },
  // Contact
  { id: 'contact.primary_name', label: 'Primary Contact Name', group: 'contact', type: 'text' },
  { id: 'contact.primary_email', label: 'Primary Contact Email', group: 'contact', type: 'text' },
  { id: 'contact.primary_type', label: 'Primary Contact Type', group: 'contact', type: 'picklist', picklistValues: ['Person', 'Company'] },
  { id: 'contact.company_name', label: 'Company Name', group: 'contact', type: 'text' },
  { id: 'contact.has_email', label: 'Has Email', group: 'contact', type: 'boolean' },
  { id: 'contact.has_mailing_address', label: 'Has Mailing Address', group: 'contact', type: 'boolean' },
  // Bill Info
  { id: 'bill.status', label: 'Bill Status', group: 'bill_info', type: 'picklist', picklistValues: ['Draft', 'Issued', 'Paid', 'Void', 'Overdue'] },
  { id: 'bill.bill_date', label: 'Bill Date', group: 'bill_info', type: 'date' },
  { id: 'bill.due_date', label: 'Due Date', group: 'bill_info', type: 'date' },
  { id: 'bill.due_amount', label: 'Due Amount', group: 'bill_info', type: 'currency' },
  { id: 'bill.total_amount', label: 'Total Amount', group: 'bill_info', type: 'currency' },
  { id: 'bill.outstanding_amount', label: 'Outstanding Amount', group: 'bill_info', type: 'currency' },
  { id: 'bill.is_overdue', label: 'Is Overdue', group: 'bill_info', type: 'boolean' },
  { id: 'bill.days_until_due', label: 'Days Until Due', group: 'bill_info', type: 'number' },
  { id: 'bill.days_past_due', label: 'Days Past Due', group: 'bill_info', type: 'number' },
  { id: 'bill.currency', label: 'Currency', group: 'bill_info', type: 'picklist', picklistValues: ['USD', 'CAD', 'GBP', 'EUR'] },
  { id: 'bill.billing_attorney', label: 'Billing Attorney', group: 'bill_info', type: 'text' },
  // Allocations / Payments
  { id: 'payment.last_payment_date', label: 'Last Payment Date', group: 'allocations', type: 'date' },
  { id: 'payment.last_payment_amount', label: 'Last Payment Amount', group: 'allocations', type: 'currency' },
  { id: 'payment.total_paid_amount', label: 'Total Paid Amount', group: 'allocations', type: 'currency' },
  { id: 'payment.allocation_count', label: 'Allocation Count', group: 'allocations', type: 'number' },
  { id: 'payment.has_any_payment', label: 'Has Any Payment', group: 'allocations', type: 'boolean' },
  { id: 'payment.days_since_last_payment', label: 'Days Since Last Payment', group: 'allocations', type: 'number' },
  // System / Derived
  { id: 'system.current_date', label: 'Current Date', group: 'system', type: 'date' },
  { id: 'system.tenant', label: 'Tenant', group: 'system', type: 'text' },
  { id: 'system.event_type', label: 'Event Type', group: 'system', type: 'text' },
  { id: 'system.event_received_date', label: 'Event Received Date', group: 'system', type: 'date' },
  { id: 'system.prior_reminder_count', label: 'Prior Reminder Count', group: 'system', type: 'number' },
  { id: 'system.last_reminder_sent_date', label: 'Last Reminder Sent Date', group: 'system', type: 'date' },
  { id: 'system.has_matching_contact_email', label: 'Has Matching Contact Email', group: 'system', type: 'boolean' },
  { id: 'system.rule_evaluation_date', label: 'Rule Evaluation Date', group: 'system', type: 'date' },
];

// ===== Operators =====

export type Operator =
  | 'equals' | 'not_equals'
  | 'contains' | 'not_contains' | 'starts_with' | 'ends_with'
  | 'gt' | 'gte' | 'lt' | 'lte' | 'between'
  | 'on' | 'before' | 'after' | 'on_or_before' | 'on_or_after'
  | 'within_last' | 'more_than_ago'
  | 'is_true' | 'is_false'
  | 'in_list' | 'not_in_list'
  | 'is_blank' | 'is_not_blank';

export interface OperatorDefinition {
  id: Operator;
  label: string;
  applicableTypes: PropertyType[];
  requiresValue: boolean;
  requiresSecondValue?: boolean;
}

export const operators: OperatorDefinition[] = [
  { id: 'equals', label: 'equals', applicableTypes: ['text', 'number', 'currency', 'picklist'], requiresValue: true },
  { id: 'not_equals', label: 'does not equal', applicableTypes: ['text', 'number', 'currency', 'picklist'], requiresValue: true },
  { id: 'contains', label: 'contains', applicableTypes: ['text'], requiresValue: true },
  { id: 'not_contains', label: 'does not contain', applicableTypes: ['text'], requiresValue: true },
  { id: 'starts_with', label: 'starts with', applicableTypes: ['text'], requiresValue: true },
  { id: 'ends_with', label: 'ends with', applicableTypes: ['text'], requiresValue: true },
  { id: 'gt', label: 'is greater than', applicableTypes: ['number', 'currency'], requiresValue: true },
  { id: 'gte', label: 'is greater than or equal to', applicableTypes: ['number', 'currency'], requiresValue: true },
  { id: 'lt', label: 'is less than', applicableTypes: ['number', 'currency'], requiresValue: true },
  { id: 'lte', label: 'is less than or equal to', applicableTypes: ['number', 'currency'], requiresValue: true },
  { id: 'between', label: 'is between', applicableTypes: ['number', 'currency'], requiresValue: true, requiresSecondValue: true },
  { id: 'on', label: 'is on', applicableTypes: ['date'], requiresValue: true },
  { id: 'before', label: 'is before', applicableTypes: ['date'], requiresValue: true },
  { id: 'after', label: 'is after', applicableTypes: ['date'], requiresValue: true },
  { id: 'on_or_before', label: 'is on or before', applicableTypes: ['date'], requiresValue: true },
  { id: 'on_or_after', label: 'is on or after', applicableTypes: ['date'], requiresValue: true },
  { id: 'within_last', label: 'is within the last', applicableTypes: ['date'], requiresValue: true },
  { id: 'more_than_ago', label: 'is more than … ago', applicableTypes: ['date'], requiresValue: true },
  { id: 'is_true', label: 'is true', applicableTypes: ['boolean'], requiresValue: false },
  { id: 'is_false', label: 'is false', applicableTypes: ['boolean'], requiresValue: false },
  { id: 'in_list', label: 'is in list', applicableTypes: ['text', 'picklist'], requiresValue: true },
  { id: 'not_in_list', label: 'is not in list', applicableTypes: ['text', 'picklist'], requiresValue: true },
  { id: 'is_blank', label: 'is blank', applicableTypes: ['text', 'number', 'currency', 'date', 'picklist'], requiresValue: false },
  { id: 'is_not_blank', label: 'is not blank', applicableTypes: ['text', 'number', 'currency', 'date', 'picklist'], requiresValue: false },
];

export function getOperatorsForType(type: PropertyType): OperatorDefinition[] {
  return operators.filter(op => op.applicableTypes.includes(type));
}

// ===== Template Classifications =====

export const templateClassifications = [
  { value: 'evergreen-invoice', label: 'Evergreen Invoice' },
  { value: 'operational-invoice', label: 'Operational Invoice' },
  { value: 'ledes-invoice', label: 'LEDES Invoice' },
  { value: 'email-invoice', label: 'Email Invoice' },
  { value: 'trust-request', label: 'Trust Request' },
  { value: 'general', label: 'General' },
];

export const daysOfWeek = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'] as const;
export type DayOfWeek = typeof daysOfWeek[number];

// ===== Helper Functions =====

export function generateRuleSummary(rule: Rule): string {
  const eventLabels = rule.eventTypes
    .map(et => eventTypes.find(e => e.id === et)?.label ?? et)
    .join(' or ');

  function summarizeGroup(group: ConditionGroup): string {
    const parts: string[] = [];

    for (const c of group.conditions) {
      const prop = propertyCatalog.find(p => p.id === c.propertyId);
      const op = operators.find(o => o.id === c.operator);
      if (!prop || !op) continue;

      if (!op.requiresValue) {
        parts.push(`${prop.label} ${op.label}`);
      } else if (typeof c.value === 'number') {
        parts.push(`${prop.label} ${op.label} ${prop.type === 'currency' ? `$${c.value}` : c.value}`);
      } else {
        parts.push(`${prop.label} ${op.label} ${c.value}`);
      }
    }

    for (const nested of group.groups) {
      parts.push(`(${summarizeGroup(nested)})`);
    }

    const joiner = group.logic === 'AND' ? ' and ' : ' or ';
    return parts.join(joiner);
  }

  const conditions = summarizeGroup(rule.conditionGroup);
  return `Use this template for ${eventLabels} events when ${conditions}.`;
}

export function ruleSnapshotToJson(snapshot: Omit<Rule, 'ruleId' | 'tenantId' | 'templateId' | 'createdBy' | 'updatedBy' | 'createdAt' | 'updatedAt'>): string {
  return JSON.stringify(snapshot, null, 2);
}
